'use client'

import { useTheme } from 'next-themes'
import type { ThemeProviderProps } from 'next-themes/dist/types'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'

import { applyPageTheme } from '@/lib/apply-page-theme'

// ssr: false is load-bearing, not just for the theme: it keeps the whole app tree
// client-only. That tree isn't SSR-safe — authAtom reads its cookie via js-cookie
// (logged-out on the server → hydration error #418 and a full client re-render),
// and the Jotai store is a module singleton that would be shared across requests.
const NextThemesProvider = dynamic(() => import('next-themes').then((e) => e.ThemeProvider), {
  ssr: false,
})

function PageThemeAttribute() {
  const { resolvedTheme, theme, setTheme } = useTheme()

  useEffect(() => {
    // System is no longer offered: pin a stored legacy `system` to what the OS resolves to now,
    // so the picker shows a real selection. (enableSystem stays on only to resolve it here.)
    if (theme === 'system') {
      if (resolvedTheme) setTheme(resolvedTheme)
      return
    }
    applyPageTheme(theme)
  }, [resolvedTheme, theme, setTheme])

  return null
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider {...props} enableColorScheme={false}>
      <PageThemeAttribute />
      {children}
    </NextThemesProvider>
  )
}
